import './data-mapping-page.css';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Dropdown, Input, notification, Select, Space, Spin, Tree } from "antd";
import { AiOutlineDelete, AiOutlineDown, AiOutlineEdit } from "react-icons/ai";
import { FaCircle, FaCircleDot, FaCircleExclamation, FaElementor, FaHashnode, FaPlus  } from "react-icons/fa6";
import { MdOutlineDataArray } from "react-icons/md";
import { RxText } from "react-icons/rx";

import { Client } from '../../model/client-model';
import { TdiNodeModel } from '../../model/tdi-node-model';
import { DataMapModel } from '../../model/data-map-model';

import { useLazyGetDataFieldsQuery, useLazyGetDataMapQuery, useSaveDataMapMutation } from '../../redux/api/data-maps';
import { useLazyGetClientQuery } from '../../redux/api/clients';
import { ConverterModal } from './converter-modal';
import { FaSearch } from 'react-icons/fa';
import { PreviewModal } from './preview-modal';

interface Props { }

const DataMappingPage: React.FC<Props> = (): JSX.Element => {

    const [loading, setLoading] = useState<boolean>();
    const [dataMapFields, setDataMapFields] = useState([]);
    const [treeData, setTreeData] = useState<TdiNodeModel[]>([]);
    const [currentNodeModel, setCurrentNodeModel] = useState<TdiNodeModel>();
    const [client, setClient] = useState<Client>();
    const [edited, setEdited] = useState<boolean>(false);
    const [isConverterOpen, setConverterOpen] = useState<boolean>(false);
    const [isPreviewOpen, setPreviewOpen] = useState<boolean>(false);

    const nodeItems = [
        {
            key: 'NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Child Node',
            children: [
                {
                    key: 'ADD_CHILD_NODE',
                    icon: <FaElementor />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_CHILD_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_CHILD_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_CHILD_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_CHILD_STATIC_TEXT',
                    icon: <RxText />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_CHILD_MAPPED_TEXT',
                    icon: <FaHashnode />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_ATTRIBUTE',
                    icon: <FaCircle color={'#015b93'}/>,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_MAPPED_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'}/>,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SEQUENCE_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'}/>,
                    label: 'Add Sequence Attribute'
                },
            ]
        },
        {
            key: 'ABOVE_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Above',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_ABOVE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_ABOVE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_ABOVE',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE',
                    icon: <FaCircleDot color={'#015b93'}/>,
                    label: 'Add Sequence Attribute'
                },
            ]
        },
        {
            key: 'BELOW_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Below',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_BELOW',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_BELOW',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_BELOW',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW',
                    icon: <FaCircleDot color={'#015b93'}/>,
                    label: 'Add Sequence Attribute'
                },
            ]
        },
        {
            key: 'CHANGE_NODE_TYPE',
            icon: <AiOutlineEdit color={'#015b93'}/>,
            label: 'Change Node Type',
            children: [
                {
                    key: 'CHANGE_TO_NODE',
                    icon: <FaElementor color={'#015b93'}/>,
                    label: 'Change to Node'
                },
                {
                    key: 'CHANGE_TO_ARRAY_NODE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Change to Array Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Phantom Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Phantom Array Node'
                },
                {
                    key: 'CHANGE_TO_STATIC_TEXT',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Change to Static Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_TEXT',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Change to Mapped Text Node'
                },
                {
                    key: 'CHANGE_TO_ATTRIBUTE',
                    icon: <FaCircle color={'#015b93'} />,
                    label: 'Change to Attribute'
                },
                {
                    key: 'CHANGE_TO_MAPPED_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Change to Mapped Attribute'
                },
                {
                    key: 'CHANGE_TO_SEQUENCE_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Change to Sequence Attribute'
                },
            ]
        },
        {
            key: 'DELETE_NODE',
            icon: <AiOutlineDelete color={'#015b93'}/>,
            label: 'Delete Node'
        },
    ]

    const textNodeItems = [
        {
            key: 'ABOVE_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Above',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_ABOVE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_ABOVE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_ABOVE',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'BELOW_NODE',
            icon: <FaPlus />,
            label: 'Add Sibling Node Below',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_BELOW',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_BELOW',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_BELOW',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'ADD_MAPPED_ATTRIBUTE',
            icon: <FaCircle color={'#015b93'} />,
            label: 'Add Mapped Attribute'
        },
        {
            key: 'ADD_ATTRIBUTE',
            icon: <FaCircle color={'#015b93'} />,
            label: 'Add Attribute'
        },
        {
            key: 'CHANGE_NODE_TYPE',
            icon: <AiOutlineEdit color={'#015b93'} />,
            label: 'Change Node Type',
            children: [
                {
                    key: 'CHANGE_TO_NODE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Change to Node'
                },
                {
                    key: 'CHANGE_TO_ARRAY_NODE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Change to Array Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Phantom Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Phantom Array Node'
                },
                {
                    key: 'CHANGE_TO_STATIC_TEXT',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Change to Static Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_TEXT',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Change to Mapped Text Node'
                },
                {
                    key: 'CHANGE_TO_ATTRIBUTE',
                    icon: <FaCircle color={'#015b93'} />,
                    label: 'Change to Attribute'
                },
                {
                    key: 'CHANGE_TO_MAPPED_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Change to Mapped Attribute'
                },
                {
                    key: 'CHANGE_TO_SEQUENCE_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Change to Sequence Attribute'
                },
            ]
        },
        {
            key: 'DELETE_NODE',
            icon: <AiOutlineDelete color={'#015b93'} />,
            label: 'Delete Node'
        },
    ]

    const sequenceNodeItems = [
        {
            key: 'ABOVE_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Above',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_ABOVE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_ABOVE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_ABOVE',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'BELOW_NODE',
            icon: <FaPlus />,
            label: 'Add Sibling Node Below',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_BELOW',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_BELOW',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_BELOW',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'ADD_MAPPED_ATTRIBUTE',
            icon: <FaCircle color={'#015b93'} />,
            label: 'Add Mapped Attribute'
        },
        {
            key: 'ADD_ATTRIBUTE',
            icon: <FaCircle color={'#015b93'} />,
            label: 'Add Attribute'
        },
        {
            key: 'CHANGE_NODE_TYPE',
            icon: <AiOutlineEdit color={'#015b93'} />,
            label: 'Change Node Type',
            children: [
                {
                    key: 'CHANGE_TO_NODE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Change to Node'
                },
                {
                    key: 'CHANGE_TO_ARRAY_NODE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Change to Array Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Phantom Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Phantom Array Node'
                },
                {
                    key: 'CHANGE_TO_STATIC_TEXT',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Change to Static Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_TEXT',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Change to Mapped Text Node'
                },
                {
                    key: 'CHANGE_TO_ATTRIBUTE',
                    icon: <FaCircle color={'#015b93'} />,
                    label: 'Change to Attribute'
                },
                {
                    key: 'CHANGE_TO_MAPPED_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Change to Mapped Attribute'
                },
                {
                    key: 'CHANGE_TO_SEQUENCE_ATTRIBUTE',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Change to Sequence Attribute'
                },
            ]
        },
        {
            key: 'DELETE_NODE',
            icon: <AiOutlineDelete color={'#015b93'} />,
            label: 'Delete Node'
        },
    ]

    const attributeItems = [
        {
            key: 'ABOVE_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Above',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_ABOVE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_ABOVE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_ABOVE',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'BELOW_NODE',
            icon: <FaPlus />,
            label: 'Add Sibling Node Below',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_BELOW',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_BELOW',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_BELOW',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_BELOW',
                    icon: <FaCircle color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW',
                    icon: <FaCircleDot color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW',
                    icon: <FaCircleExclamation color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'CHANGE_NODE_TYPE',
            icon: <AiOutlineEdit />,
            label: 'Change Node Type',
            children: [
                {
                    key: 'CHANGE_TO_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Node'
                },
                {
                    key: 'CHANGE_TO_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Array Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Phantom Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Phantom Array Node'
                },
                {
                    key: 'CHANGE_TO_STATIC_TEXT',
                    icon: <RxText />,
                    label: 'Change to Static Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_TEXT',
                    icon: <FaHashnode />,
                    label: 'Change to Mapped Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_ATTRIBUTE',
                    icon: <FaCircleDot />,
                    label: 'Change to Mapped Attribute'
                },
                {
                    key: 'CHANGE_TO_SEQUENCE_ATTRIBUTE',
                    icon: <FaCircleExclamation />,
                    label: 'Change to Sequence Attribute'
                }
            ]
        },
        {
            key: 'DELETE_NODE',
            icon: <AiOutlineDelete />,
            label: 'Delete Node'
        },
    ]

    const mappedAttributeItems = [
        
        {
            key: 'ABOVE_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Above',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_ABOVE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_ABOVE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_ABOVE',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'BELOW_NODE',
            icon: <FaPlus />,
            label: 'Add Sibling Node Below',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_BELOW',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_BELOW',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_BELOW',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'CHANGE_NODE_TYPE',
            icon: <AiOutlineEdit />,
            label: 'Change Node Type',
            children: [
                {
                    key: 'CHANGE_TO_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Node'
                },
                {
                    key: 'CHANGE_TO_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Array Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Phantom Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Phantom Array Node'
                },
                {
                    key: 'CHANGE_TO_STATIC_TEXT',
                    icon: <RxText />,
                    label: 'Change to Static Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_TEXT',
                    icon: <FaHashnode />,
                    label: 'Change to Mapped Text Node'
                },
                {
                    key: 'CHANGE_TO_ATTRIBUTE',
                    icon: <FaCircle />,
                    label: 'Change to Attribute'
                },
                {
                    key: 'CHANGE_TO_SEQUENCE_ATTRIBUTE',
                    icon: <FaCircleDot />,
                    label: 'Change to Sequence Attribute'
                }
            ]
        },
        {
            key: 'DELETE_NODE',
            icon: <AiOutlineDelete />,
            label: 'Delete Node'
        },
    ]

    const sequenceAttributeItems = [
        
        {
            key: 'ABOVE_NODE',
            icon: <FaPlus color={'#015b93'} />,
            label: 'Add Sibling Node Above',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_ABOVE',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_ABOVE',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_ABOVE',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'BELOW_NODE',
            icon: <FaPlus />,
            label: 'Add Sibling Node Below',
            children: [
                {
                    key: 'ADD_SIBLING_NODE_BELOW',
                    icon: <FaElementor color={'#015b93'} />,
                    label: 'Add Node'
                },
                {
                    key: 'ADD_SIBLING_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray color={'#015b93'} />,
                    label: 'Add Array Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_NODE_BELOW',
                    icon: <FaElementor />,
                    label: 'Add Phantom Node'
                },
                {
                    key: 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW',
                    icon: <MdOutlineDataArray />,
                    label: 'Add Phantom Array Node'
                },
                {
                    key: 'ADD_SIBLING_STATIC_TEXT_BELOW',
                    icon: <RxText color={'#015b93'} />,
                    label: 'Add Static Text Node'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_TEXT_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Node'
                },
                {
                    key: 'ADD_SIBLING_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Attribute'
                },
                {
                    key: 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Mapped Attribute'
                },
                {
                    key: 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW',
                    icon: <FaHashnode color={'#015b93'} />,
                    label: 'Add Sequence Attribute'
                }
            ]
        },
        {
            key: 'CHANGE_NODE_TYPE',
            icon: <AiOutlineEdit />,
            label: 'Change Node Type',
            children: [
                {
                    key: 'CHANGE_TO_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Node'
                },
                {
                    key: 'CHANGE_TO_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Array Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_NODE',
                    icon: <FaElementor />,
                    label: 'Change to Phantom Node'
                },
                {
                    key: 'CHANGE_TO_PHANTOM_ARRAY_NODE',
                    icon: <MdOutlineDataArray />,
                    label: 'Change to Phantom Array Node'
                },
                {
                    key: 'CHANGE_TO_STATIC_TEXT',
                    icon: <RxText />,
                    label: 'Change to Static Text Node'
                },
                {
                    key: 'CHANGE_TO_MAPPED_TEXT',
                    icon: <FaHashnode />,
                    label: 'Change to Mapped Text Node'
                },
                {
                    key: 'CHANGE_TO_ATTRIBUTE',
                    icon: <FaCircle />,
                    label: 'Change to Attribute'
                },
                {
                    key: 'CHANGE_TO_MAPPED_ATTRIBUTE',
                    icon: <FaCircleDot />,
                    label: 'Change to Mapped Attribute'
                }
            ]
        },
        {
            key: 'DELETE_NODE',
            icon: <AiOutlineDelete />,
            label: 'Delete Node'
        },
    ]

    const { clientId } = useParams();

    const [getClient] = useLazyGetClientQuery();
    const [getDataMapFields] = useLazyGetDataFieldsQuery();
    const [getDataMap] = useLazyGetDataMapQuery();
    const [saveDataMap] = useSaveDataMapMutation();

    useEffect(() => {

        const fetchDataMapFields = async () => {

            try {
                setLoading(true);
                const data = await getDataMapFields(clientId).unwrap();
                let options = data.map((field: string) => {
                    return { value: field, label: field };
                })
                options.unshift({value: '', label: ''});
                setDataMapFields(options);
                setLoading(false);
            } catch (e) {
                console.log(e);
                notification.error({
                    message: "Data Fields Error!",
                    description: `An error occurred while fetching the trusted driver data fields.`
                });
            }
        }

        const fetchClient = async () => {
            try {
                const data = await getClient(clientId).unwrap();
                setClient(data);
            } catch (e) {
                console.log(e);
                notification.error({
                    message: "Clients Error!",
                    description: `An error occurred while fetching the clients.`
                });
            }
        }

        fetchDataMapFields();
        fetchClient();

    }, []);

    useEffect(() => {
        
        const fetchDataMaps = async () => {

            const initializeTree = (nodes?: DataMapModel[]): TdiNodeModel[] => {

                const children = nodes?.map((item: DataMapModel) => {
                    let dataMap = null;
                    switch (item?.type) {
                        case 'ARRAY_NODE': dataMap = createArrayNode(undefined, item); break;
                        case 'ATTRIBUTE': dataMap = createAttribute(undefined, item); break;
                        case 'MAPPED_ATTRIBUTE': dataMap = createMappedAttribute(undefined, item); break;
                        case 'MAPPED_TEXT': dataMap = createMappedTextNode(undefined, item); break;
                        case 'PHANTOM_ARRAY_NODE': dataMap = createPhantomArrayNode(undefined, item); break;
                        case 'PHANTOM_NODE': dataMap = createPhantomNode(undefined, item); break;
                        case 'SEQUENCE_ATTRIBUTE': dataMap = createSequenceAttribute(undefined, item); break;
                        case 'SEQUENCE_TEXT': dataMap = createSequenceTextNode(undefined, item); break;
                        case 'STATIC_TEXT': dataMap = createStaticTextNode(undefined, item); break;
                        default: dataMap = createNode(undefined, item)
                    }
                    dataMap.children = item.children ? initializeTree(item.children) : [];
                    return dataMap;
                });
                return children ? children : [];
            }

            try {
                const dataMap = await getDataMap(clientId).unwrap();
                if (dataMap && dataMap.children && dataMap.children.length > 0) {
                    const data = initializeTree(dataMap.children);
                    setTreeData(data);
                }
            } catch (e) {
                console.log(e);
                notification.error({
                    message: "Data Map Configuration Error!",
                    description: `An error occurred while fetching the data map configuration.`
                });
            }
        }

        if (dataMapFields.length !== 0) {
            fetchDataMaps();
        }

    }, [dataMapFields])

    /**
     * Recursive function to traverse the tree node and find the node equivalent to
     * the node.
     * 
     * @param node the node find
     * @param nodes the tree nodes
     * @returns 
     */
    //@ts-ignore
    const findNodeFromTree = (node: TdiNodeModel, nodes: TdiNodeModel[]): TdiNodeModel => {
        for (let i = 0; i < nodes.length; i++) {
            let item = nodes[i];
            if (item.key.toString() === node.key.toString()) {
                return item;
            }
            if (item.children) {
                let foundItem = findNodeFromTree(node, item.children);
                if (foundItem) {
                    return foundItem;
                }
            }
        }
    }

    /**
     * Recursive function to traverse the tree node and find the parent node given the node's
     * parent key.
     */

    //@ts-ignore
    const findParentFromTree = (parentKey?: string, nodes: TdiNodeModel[]): TdiNodeModel => {
        for (let i = 0; i < nodes.length; i++) {
            let item = nodes[i];
            if (item.key.toString() === parentKey) {
                return item;
            }
            if (item.children) {
                let foundItem = findParentFromTree(parentKey, item.children);
                if (foundItem) {
                    return foundItem;
                }
            }
        }
    }

    /**
     * Adds a child node to the parent node
     * @param node the parent node
     * @param childNode the node to add
     */
    const addToParentNode = (parentNode: TdiNodeModel, childNode: TdiNodeModel) => {

        setTreeData((tree) => {
            let clonedTree = [...tree];
            let children = parentNode.children ? [...parentNode.children, childNode] : [childNode];
            let foundNode: TdiNodeModel = findNodeFromTree(parentNode, clonedTree);
            if (foundNode) {
                foundNode.children = children;
            }
            return clonedTree;
        });
    }

    /**
     * Add a node above the selected node.
     * 
     * @param selectedNode the selected node
     * @param siblingNode the sibling node to add above
     */
    const addNodeAbove = (selectedNode: TdiNodeModel, siblingNode: TdiNodeModel) => {

        setTreeData((tree) => {
            let clonedTree = [...tree];
            let parentNode: TdiNodeModel = findParentFromTree(selectedNode.parentKey, clonedTree);
            if (parentNode) {
                let children = [...parentNode.children];
                const ndx = children.findIndex((child) => {
                    return child.key.toString() === selectedNode.key.toString()
                });
                children.splice(ndx, 0, siblingNode);
                parentNode.children = children;
                return clonedTree;
            } else {
                let clonedTree = [...tree];
                const ndx = clonedTree.findIndex((child) => child.key.toString() === selectedNode.key.toString());
                clonedTree.splice(ndx, 0, siblingNode);
                return clonedTree;
            }
        });
    }

    /**
     * Add a node below the selected node.
     * 
     * @param selectedNode the selected node
     * @param siblingNode the sibling node to add below
     */
    const addNodeBelow = (selectedNode: TdiNodeModel, siblingNode: TdiNodeModel) => {

        setTreeData((tree) => {
            let clonedTree = [...tree];
            let parentNode: TdiNodeModel = findParentFromTree(selectedNode.parentKey, clonedTree);
            if (parentNode) {
                let children = [...parentNode.children];
                const ndx = children.findIndex((child) => {
                    return child.key.toString() === selectedNode.key.toString()
                });
                if (ndx !== children.length -1) {
                    children.splice(ndx + 1, 0, siblingNode);
                } else {
                    children.push(siblingNode);
                }
                parentNode.children = children;
                return clonedTree;
            } else {
                let clonedTree = [...tree];
                const ndx = clonedTree.findIndex((child) => child.key.toString() === selectedNode.key.toString());
                if (ndx !== clonedTree.length - 1) {
                    clonedTree.splice(ndx + 1, 0, siblingNode);
                } else {
                    clonedTree.push(siblingNode);
                }
                return clonedTree;
            }
        });
    }

    /**
     * Handles a node action whether to add a node, add a text node, or to delete a node.
     * 
     * @param menuKey the identifier of the menu item that received a click.
     * @param node the node associated with the action.
     */
    const handleNodeAction = (menuKey: string, node: TdiNodeModel) => {

        switch (menuKey) {

            case 'ADD_CHILD_NODE':
                const childNode = createNode(node.key.toString());
                addToParentNode(node, childNode);
                break;

            case 'ADD_CHILD_ARRAY_NODE':
                const childArrayNode = createArrayNode(node.key.toString());
                addToParentNode(node, childArrayNode);
                break;

            case 'ADD_CHILD_PHANTOM_ARRAY_NODE':
                const childPhantomArrayNode = createPhantomArrayNode(node.key.toString());
                addToParentNode(node, childPhantomArrayNode);
                break;

            case 'ADD_CHILD_PHANTOM_NODE':
                const childPhantomNode = createPhantomNode(node.key.toString());
                addToParentNode(node, childPhantomNode);
                break;

            case 'ADD_CHILD_MAPPED_TEXT':
                const childMappedTextNode = createMappedTextNode(node.key.toString());
                addToParentNode(node, childMappedTextNode);
                break;

            case 'ADD_CHILD_STATIC_TEXT':
                const childStaticTextNode = createStaticTextNode(node.key.toString());
                addToParentNode(node, childStaticTextNode);
                break;

            case 'ADD_ATTRIBUTE':
                const childAttribute = createAttribute(node.key.toString());
                addToParentNode(node, childAttribute);
                break;
                    
            case 'ADD_MAPPED_ATTRIBUTE':
                const childMappedAttribute = createMappedAttribute(node.key.toString());
                addToParentNode(node, childMappedAttribute);
                break;
    
            case 'ADD_SEQUENCE_ATTRIBUTE':
                const childSequenceAttribute = createSequenceAttribute(node.key.toString());
                addToParentNode(node, childSequenceAttribute);
                break;

            case 'ADD_SIBLING_NODE_ABOVE':
                const siblingNodeAbove = createNode(node.parentKey?.toString());
                addNodeAbove(node, siblingNodeAbove);
                break;

            case 'ADD_SIBLING_ARRAY_NODE_ABOVE':
                const siblingArrayNodeAbove = createArrayNode(node.parentKey?.toString());
                addNodeAbove(node, siblingArrayNodeAbove);
                break;

            case 'ADD_SIBLING_PHANTOM_NODE_ABOVE':
                const siblingPhantomNodeAbove = createPhantomNode(node.parentKey?.toString());
                addNodeAbove(node, siblingPhantomNodeAbove);
                break;

            case 'ADD_SIBLING_PHANTOM_ARRAY_NODE_ABOVE':
                const siblingPhantomArrayNodeAbove = createPhantomArrayNode(node.parentKey?.toString());
                addNodeAbove(node, siblingPhantomArrayNodeAbove);
                break;

            case 'ADD_SIBLING_STATIC_TEXT_ABOVE':
                const siblingStaticTextAbove = createStaticTextNode(node.parentKey?.toString());
                addNodeAbove(node, siblingStaticTextAbove);
                break;
            
            case 'ADD_SIBLING_MAPPED_TEXT_ABOVE':
                const siblingMappedTextAbove = createMappedTextNode(node.parentKey?.toString());
                addNodeAbove(node, siblingMappedTextAbove);
                break;

            case 'ADD_SIBLING_ATTRIBUTE_ABOVE':
                const siblingAttributeAbove = createAttribute(node.parentKey?.toString());
                addNodeAbove(node, siblingAttributeAbove);
                break;
            
            case 'ADD_SIBLING_MAPPED_ATTRIBUTE_ABOVE':
                const siblingMappedAtrributeAbove = createMappedAttribute(node.parentKey?.toString());
                addNodeAbove(node, siblingMappedAtrributeAbove);
                break;

            case 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_ABOVE':
                const siblingSequenceAtrributeAbove = createSequenceAttribute(node.parentKey?.toString());
                addNodeAbove(node, siblingSequenceAtrributeAbove);
                break;

            case 'ADD_SIBLING_NODE_BELOW':
                const siblingNodeBelow = createNode(node.parentKey?.toString());
                addNodeBelow(node, siblingNodeBelow);
                break;

            case 'ADD_SIBLING_ARRAY_NODE_BELOW':
                const siblingArrayNodeBelow = createArrayNode(node.parentKey?.toString());
                addNodeBelow(node, siblingArrayNodeBelow);
                break;

            case 'ADD_SIBLING_PHANTOM_NODE_BELOW':
                const siblingPhantomNodeBelow = createPhantomNode(node.parentKey?.toString());
                addNodeBelow(node, siblingPhantomNodeBelow);
                break;

            case 'ADD_SIBLING_PHANTOM_ARRAY_NODE_BELOW':
                const siblingPhantomArrayNodeBelow = createPhantomArrayNode(node.parentKey?.toString());
                addNodeBelow(node, siblingPhantomArrayNodeBelow);
                break;
                    
            case 'ADD_SIBLING_STATIC_TEXT_BELOW':
                const siblingStaticTextBelow = createStaticTextNode(node.parentKey?.toString());
                addNodeBelow(node, siblingStaticTextBelow);
                break;
            
            case 'ADD_SIBLING_MAPPED_TEXT_BELOW':
                const siblingMappedTextBelow = createMappedTextNode(node.parentKey?.toString());
                addNodeBelow(node, siblingMappedTextBelow);
                break;

            case 'ADD_SIBLING_ATTRIBUTE_BELOW':
                const siblingAttributeBelow = createAttribute(node.parentKey?.toString());
                addNodeBelow(node, siblingAttributeBelow);
                break;
            
            case 'ADD_SIBLING_MAPPED_ATTRIBUTE_BELOW':
                const siblingMappedAtrributeBelow = createMappedAttribute(node.parentKey?.toString());
                addNodeBelow(node, siblingMappedAtrributeBelow);
                break;

            case 'ADD_SIBLING_SEQUENCE_ATTRIBUTE_BELOW':
                const siblingSequenceAtrributeBelow = createMappedAttribute(node.parentKey?.toString());
                addNodeBelow(node, siblingSequenceAtrributeBelow);
                break;

            case 'CHANGE_TO_NODE': 
                changeNodeType(node, 'NODE');
                break;

            case 'CHANGE_TO_ARRAY_NODE': 
                changeNodeType(node, 'ARRAY_NODE');
                break;

            case 'CHANGE_TO_PHANTOM_NODE': 
                changeNodeType(node, 'PHANTOM_NODE');
                break;

            case 'CHANGE_TO_PHANTOM_ARRAY_NODE': 
                changeNodeType(node, 'PHANTOM_ARRAY_NODE');
                break;

            case 'CHANGE_TO_STATIC_TEXT': 
                changeNodeType(node, 'STATIC_TEXT');
                break;

            case 'CHANGE_TO_MAPPED_TEXT': 
                changeNodeType(node, 'MAPPED_TEXT');
                break;

            case 'CHANGE_TO_MAPPED_ATTRIBUTE': 
                changeNodeType(node, 'MAPPED_ATTRIBUTE');
                break;

            case 'CHANGE_TO_SEQUENCE_ATTRIBUTE': 
                console.log('Changing to sequence');
                changeNodeType(node, 'SEQUENCE_ATTRIBUTE');
                break;

            case 'CHANGE_TO_ATTRIBUTE': 
                changeNodeType(node, 'ATTRIBUTE');
                break;

            case 'DELETE_NODE':
                setTreeData((tree) => {
                    let clonedTree = [...tree];
                    let parentNode: TdiNodeModel = findParentFromTree(node.parentKey, clonedTree);
                    if (parentNode) {
                        let children = [...parentNode.children];
                        const ndx = children.findIndex((child) => child.key.toString() === node.key.toString());
                        children.splice(ndx, 1);
                        parentNode.children = children;
                        return clonedTree;
                    } else {
                        let clonedTree = [...tree];
                        const ndx = clonedTree.findIndex((child) => child.key.toString() === node.key.toString());
                        clonedTree.splice(ndx, 1);
                        return clonedTree;
                    }
                });
                break;
        }

        if (!edited) setEdited(true);
    }

    const nodeDropDown = (node: TdiNodeModel) => {

        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <FaElementor size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const phantomNodeDropDown = (node: TdiNodeModel) => {

        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <FaElementor size={18} color={'gray'}/>
                        <Input defaultValue={'phantom node'} className="input-item" disabled/>
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const arrayNodeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <MdOutlineDataArray size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const phantomArrayNodeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: nodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <MdOutlineDataArray size={18} color={'gray'}/>
                        <Input defaultValue={'phantom array'} className="input-item" disabled/>
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const sequenceTextDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: sequenceNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxText size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const staticTextDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: textNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <RxText size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const mappedTextDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: textNodeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <FaHashnode size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                        <Button type="primary" onClick={() => openConverter(node)}>
                            Converter
                        </Button>
                        {node.required && <span style={{color: 'red'}}>required</span>}
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const attributeDropDown = (node: TdiNodeModel) => {
       return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: attributeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-row">
                        <FaCircle size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>;
    }

    const mappedAttributeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: mappedAttributeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <FaCircleDot size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Select defaultValue={node.source} className="select-item"
                            options={dataMapFields}
                            onChange={(text) => {
                                node.source = text;
                                if (!edited) setEdited(true);
                            }} />
                        <Button type="primary" onClick={() => openConverter(node)}>
                            Converter
                        </Button>
                        {node.required && <span style={{color: 'red'}}>required</span>}
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    const sequenceAttributeDropDown = (node: TdiNodeModel) => {
        return <Dropdown
            trigger={['contextMenu']}
            menu={{
                items: sequenceAttributeItems,
                onClick: ({ key }) => handleNodeAction(key, node)
            }}>
            <div style={{ cursor: 'pointer' }} onClick={(e) => e.preventDefault()}>
                <Space>
                    <div className="field-map-row">
                        <FaCircleExclamation size={18} color={'#015b93'}/>
                        <Input defaultValue={node.target} className="input-item"
                            onChange={(e) => {
                                node.target = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                        <Input defaultValue={node.source} className="select-item"
                            onChange={(e) => {
                                node.source = e.target.value;
                                if (!edited) setEdited(true);
                            }} />
                    </div>
                </Space>
            </div>
        </Dropdown>
    }

    /**
     * Creates a Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let childNode: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'NODE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        childNode.title = nodeDropDown(childNode);
        return childNode;
    }

    /**
     * Creates a Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createPhantomNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let childNode: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'PHANTOM_NODE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        childNode.title = nodeDropDown(childNode);
        return childNode;
    }

    /**
     * Creates an Array Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createArrayNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'ARRAY_NODE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = arrayNodeDropDown(node);
        return node;
    }

    /**
     * Creates an Array Node.
     * 
     * @param parentKey the key of the parent to add the node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the node model
     */
    const createPhantomArrayNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'PHANTOM_ARRAY_NODE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = phantomArrayNodeDropDown(node);
        return node;
    }
    
    const openConverter = (node:TdiNodeModel) => {
        setCurrentNodeModel(node);
        setConverterOpen(true);
    }

    /**
     * Creates a Mapped Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createMappedTextNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'MAPPED_TEXT',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: dataMapModel?.required,
            children: [],
            converterType: dataMapModel?.converterType || 'NONE',
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = mappedTextDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createSequenceTextNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'SEQUENCE_TEXT',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = sequenceTextDropDown(node);
        return node;
    }

    /**
     * Creates a Mapped Text Node.
     * 
     * @param parentKey the key of the parent to add the text node to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createStaticTextNode = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'STATIC_TEXT',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: true,
            children: [],
        }

        node.title = staticTextDropDown(node);
        return node;
    }

    /**
     * changes the node type to {type}.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const changeNodeType = (node: TdiNodeModel, nodeType: string): TdiNodeModel => {

        node.type = nodeType;

        switch (nodeType) {
            case 'NODE': node.title = nodeDropDown(node); break;
            case 'ARRAY_NODE': node.title = arrayNodeDropDown(node); break;
            case 'ATTRIBUTE': node.title = attributeDropDown(node); break;
            case 'PHANTOM_NODE': node.title = phantomNodeDropDown(node); break;
            case 'PHANTOM_ARRAY_NODE': node.title = phantomArrayNodeDropDown(node); break;
            case 'STATIC_TEXT': node.title = staticTextDropDown(node); break;
            case 'MAPPED_TEXT': node.title = mappedTextDropDown(node); break;
            case 'MAPPED_ATTRIBUTE': node.title = mappedAttributeDropDown(node); break;
            case 'SEQUENCE_ATTRIBUTE': node.title = sequenceAttributeDropDown(node); break;
        }
        
        setTreeData((tree) => {
            let clonedTree = [...tree];
            let parentNode: TdiNodeModel = findParentFromTree(node.parentKey, clonedTree);
            if (parentNode) {
                let children = [...parentNode.children];
                const ndx = children.findIndex((child) => child.key.toString() === node.key.toString());
                children.splice(ndx, 1, node);
                parentNode.children = children;
                return clonedTree;
            } else {
                let clonedTree = [...tree];
                const ndx = clonedTree.findIndex((child) => child.key.toString() === node.key.toString());
                clonedTree.splice(ndx, 1, node);
                return clonedTree;
            }
        });
        
        return node;
    }

    /**
     * Creates an Attribute.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createAttribute = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'ATTRIBUTE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: false,
            children: [],
            enums: [],
        }

        node.title = attributeDropDown(node);

        return node;
    }

    /**
     * Creates an Attribute.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createMappedAttribute = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'MAPPED_ATTRIBUTE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: false,
            converterType: dataMapModel?.converterType || 'NONE',
            children: [],
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = mappedAttributeDropDown(node);

        return node;
    }

    /**
     * Creates an Attribute.
     * 
     * @param parentKey the key of the parent to add the attribute to
     * @param dataMapModel the model from the database to map to a node, if existing.
     * @returns the text node model
     */
    const createSequenceAttribute = (parentKey?: string, dataMapModel?: DataMapModel): TdiNodeModel => {

        let node: TdiNodeModel = {
            parentKey: dataMapModel?.parentKey || parentKey,
            type: 'SEQUENCE_ATTRIBUTE',
            key: dataMapModel?.key || crypto.randomUUID(),
            source: dataMapModel?.source,
            target: dataMapModel?.target,
            required: false,
            converterType: dataMapModel?.converterType || 'NONE',
            children: [],
            script: dataMapModel?.script,
            enums: dataMapModel?.enums,
        }

        node.title = sequenceAttributeDropDown(node);

        return node;
    }

    /**
     * Adds a root node.
     * Called when the button at the top left for adding a root node is clicked.
     */
    const addRootNode = () => {
        const nodeModel = createNode();
        const data = [...treeData, nodeModel];
        setTreeData(data);
        if (!edited) setEdited(true);
    }

    /**
     * Adds an root array node.
     * Called when the button at the top left for adding a root node is clicked.
     */
    const addRootArrayNode = () => {
        const nodeModel = createArrayNode();
        const data = [...treeData, nodeModel];
        setTreeData(data);
        if (!edited) setEdited(true);
    }

    /**
     * Adds a root text node.
     * Called when the button at the top left for adding a root text node is clicked.
     */
    const addRootMappedTextNode = () => {
        const nodeModel = createMappedTextNode();
        const data = [...treeData, nodeModel];
        setTreeData(data);
        if (!edited) setEdited(true);
    }

    /**
     * Adds a root text node.
     * Called when the button at the top left for adding a root text node is clicked.
     */
    const addRootStaticTextNode = () => {
        const nodeModel = createStaticTextNode();
        const data = [...treeData, nodeModel];
        setTreeData(data);
        if (!edited) setEdited(true);
    }

    const previewXML = () => {
        setPreviewOpen(true);
    }

    /**
     * Saves the data mapping.
     */
    const saveDataMapping = async () => {

        /**
         * Recursively traverses the tree to convert the data into a format the the server
         * understands and can save.
         * 
         * @param nodes 
         * @param parent 
         * @returns 
         */
        const buildDataMapModel = (nodes: TdiNodeModel[], parent?: DataMapModel): DataMapModel[] => {

            const children = nodes.map((item: TdiNodeModel) => {
                let dataMap: DataMapModel = {
                    key: item?.key.toString(),
                    parentKey: item?.parentKey,
                    type: item.type,
                    source: item.source,
                    target: item.target,
                    required: item.required,
                    converterType: item.converterType,
                    script: item.script,
                    enums: item.enums?.map((item: DataMapModel) => {
                        return {source: item.source, target: item.target};
                    })
                }
                dataMap.children = buildDataMapModel(item.children, dataMap);
                return dataMap;
            });
            return children;
        }

        const children = buildDataMapModel(treeData);
        const dataMap = {
            client: client,
            children: children,
        }

        try {
            await saveDataMap(dataMap).unwrap();
            setEdited(false);
            notification.success({
                message: "Data Mapping Saved!",
                description: `The data mapping configuration has been created.`
            });
        } catch (e) {
            console.log(e);
            notification.error({
                message: "Saving Data Mapping Failed!",
                description: `An error occurred while saving the data mapping configuration.`
            });
        }
    }

    const saveConverter = (values: any) => {
        
        if (currentNodeModel) {
            const data = [...treeData];
            let node = findNodeFromTree(currentNodeModel, data);
            node.required = values.required;
            node.converterType = values.converterType;
            node.enums = values.enums?.map((item: any) => {
                return {source: item.source, target: item.target}
            });
            node.source = values.source;
            node.script = values.script;
            setTreeData(data);
        }
        if (!edited) setEdited(true);
        setConverterOpen(false);
    }

    return <div className="data-mapping">
        <div className="data-mapping-header">
            <h1>Data Mapping - {client?.name}</h1>
            <span style={{ display: 'flex', gap: '.5rem' }}>
                <Button type="primary" onClick={addRootNode} icon={<FaElementor />}>
                    Add Root Node
                </Button>
                <Button type="primary" onClick={addRootArrayNode} icon={<MdOutlineDataArray />}>
                    Add Root Array Node
                </Button>
                {/* <Button type="primary" onClick={addRootMappedTextNode} icon={<FaHashnode />}>
                    Add Mapped Text Node
                </Button>
                <Button type="primary" onClick={addRootStaticTextNode} icon={<RxText />}>
                    Add Static Text Node
                </Button> */}
                <span style={{width: '3rem'}}></span>
                <Button type="primary" onClick={previewXML} icon={<FaSearch />}>
                    Preview
                </Button>
            </span>
        </div>
        {treeData.length !== 0 && (
            <div className="data-mapping-tree-content">
                <Tree
                    showIcon
                    defaultExpandAll
                    switcherIcon={<AiOutlineDown />}
                    treeData={treeData}
                />
            </div>
        )}
        
        {loading && 
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32}}>
                <Spin tip="Loading" size={"large"}/>
            </div>
        }

        <div className="data-mapping-control">
            <Button
                type="primary"
                onClick={saveDataMapping}
                disabled={!edited}
            >
                Save Data Mapping
            </Button>
        </div>

        <ConverterModal
            open={isConverterOpen}
            onCancel={() => setConverterOpen(false)}
            node={currentNodeModel}
            onOk={saveConverter}
        />

        <PreviewModal
            client={client}
            open={isPreviewOpen}
            onCancel={() => setPreviewOpen(false)}
        />
    </div>

}

export default DataMappingPage;
